import clsx from "clsx";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import styles from "./game.module.css";

// Игровые компоненты. Всё, что видит игрок вне самих механик, собрано здесь,
// чтобы у карты уровней и у экрана победы был один язык.

const HeartIcon = ({ filled = true, size = 18 }: { filled?: boolean; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 21s-7.5-4.6-9.3-9A5.4 5.4 0 0 1 12 6.6 5.4 5.4 0 0 1 21.3 12c-1.8 4.4-9.3 9-9.3 9z"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const BulbIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M9 21c0 .6.4 1 1 1h4c.6 0 1-.4 1-1v-1H9v1zm3-19C8.1 2 5 5.1 5 9c0 2.4 1.2 4.5 3 5.7V17c0 .6.4 1 1 1h6c.6 0 1-.4 1-1v-2.3c1.8-1.3 3-3.4 3-5.7 0-3.9-3.1-7-7-7z" />
  </svg>
);

const StarIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="m12 17.3-6.2 3.7 1.7-7L2 9.2l7.2-.6L12 2l2.8 6.6 7.2.6-5.5 4.8 1.7 7z" />
  </svg>
);

export function LivesCounter({
  lives,
  max,
  nextLifeIn,
}: {
  lives: number;
  max: number;
  /** секунд до следующей жизни */
  nextLifeIn?: number | null;
}) {
  const timer =
    nextLifeIn != null && lives < max
      ? `${Math.floor(nextLifeIn / 60)}:${String(nextLifeIn % 60).padStart(2, "0")}`
      : null;

  return (
    <span className={styles.counter} title={`Жизни: ${lives} из ${max}`}>
      <span className={styles.counterIcon}>
        <HeartIcon filled={lives > 0} />
      </span>
      {lives}
      {timer ? <span className={styles.counterTimer}>+1 через {timer}</span> : null}
    </span>
  );
}

export interface HintCounterProps {
  hints: number;
  /** Задан — счётчик становится кнопкой «взять подсказку» */
  onUse?: () => void;
  disabled?: boolean;
  busy?: boolean;
}

export function HintCounter({ hints, onUse, disabled, busy }: HintCounterProps) {
  const content = (
    <>
      <span className={styles.counterHint}>
        <BulbIcon />
      </span>
      {busy ? "…" : hints}
    </>
  );

  if (!onUse) {
    return (
      <span className={styles.counter} title={`Подсказок: ${hints}`}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={clsx(styles.counter, styles.counterButton)}
      onClick={onUse}
      disabled={disabled || busy || hints <= 0}
      title={hints > 0 ? "Взять подсказку" : "Подсказки кончились"}
    >
      {content}
    </button>
  );
}

export function StarRating({ value, max = 3, size }: { value: number; max?: number; size?: number }) {
  return (
    <span className={styles.stars} aria-label={`Звёзд: ${value} из ${max}`}>
      {Array.from({ length: max }).map((_, index) => (
        <span key={index} className={index < value ? styles.starOn : undefined}>
          <StarIcon size={size} />
        </span>
      ))}
    </span>
  );
}

export function PixelPanel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx(styles.panel, className)}>{children}</div>;
}

export interface NeonButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: "cyan" | "magenta";
}

export function NeonButton({ tone = "cyan", className, children, ...rest }: NeonButtonProps) {
  return (
    <button
      type="button"
      className={clsx(styles.neon, tone === "magenta" && styles.neonMagenta, className)}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Круглая кнопка поверх сцены: выход, звук, пропуск ролика */
export function GameIconButton({
  label,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      className={clsx(styles.iconButton, className)}
      aria-label={label}
      title={label}
      {...rest}
    >
      {children}
    </button>
  );
}

export const BackIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function ChapterCard({
  title,
  subtitle,
  coverUrl,
  locked,
  lockHint,
  progress,
  children,
  onClick,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  coverUrl?: string | null;
  locked?: boolean;
  lockHint?: ReactNode;
  progress?: ReactNode;
  children?: ReactNode;
  onClick?: () => void;
}) {
  return (
    <section
      className={clsx(styles.chapter, locked && styles.chapterLocked)}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      {coverUrl ? <img src={coverUrl} alt="" className={styles.chapterCover} /> : null}
      <span className={styles.chapterShade} />
      <div className={styles.chapterBody}>
        <h2 className={styles.chapterTitle}>{title}</h2>
        {subtitle ? <p className={styles.chapterSubtitle}>{subtitle}</p> : null}
        {locked && lockHint ? <p className={styles.chapterSubtitle}>Закрыто: {lockHint}</p> : null}
        {progress}
        {children}
      </div>
    </section>
  );
}

export function LevelCard({
  index,
  title,
  meta,
  status,
  stars,
  onClick,
}: {
  index: number;
  title: ReactNode;
  meta?: ReactNode;
  status: "locked" | "available" | "completed";
  stars?: number;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className={styles.level}
      disabled={status === "locked"}
      onClick={onClick}
    >
      <span className={clsx(styles.levelIndex, status === "completed" && styles.levelDone)}>
        {status === "locked" ? "🔒" : index}
      </span>
      <span className={styles.levelBody}>
        <span className={styles.levelTitle}>{title}</span>
        {meta ? <span className={styles.levelMeta}>{meta}</span> : null}
      </span>
      {status === "completed" ? <StarRating value={stars ?? 0} /> : null}
    </button>
  );
}

export function GameHud({
  left,
  title,
  right,
}: {
  left?: ReactNode;
  title?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <header className={styles.hud}>
      <span className={styles.hudSide}>{left}</span>
      {title ? <span className={styles.hudTitle}>{title}</span> : null}
      <span className={styles.hudSide}>{right}</span>
    </header>
  );
}
